import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
} from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import type {
  AdHocPlaceOfSafetyUnbranded,
  CuratedPlaceOfSafetyUnbranded,
} from "@thalamos/common";
import { normaliseForSearch } from "@thalamos/common";
import type {
  IncidentWorkItemWithContext,
  TranslationKey,
  TranslationKeyAndOptions,
  TranslationOptions,
} from "@thalamos/vision-common";
import { getWorkflowTranslationContext, tk } from "@thalamos/vision-common";

import { createPlaceSearch } from "../../../lib/search/placeSearch.js";
import { Banner } from "../../Banner/Banner.js";
import { Typeahead } from "../../Typeahead/Typeahead.js";
import { FormError } from "../FormError/FormError.js";
import { FormLabel } from "../FormLabel/FormLabel.js";
import { CollectAdHocPlaceOfSafety } from "./CollectAdHocPlaceOfSafety.js";
import { usePlacesOfSafety } from "./PlaceOfSafetyContext.js";
import { useAdHocPlaceOfSafety } from "./useAdHocPlaceOfSafety.js";
import { useOutsideForceAreaWarning } from "./useOutsideForceAreaWarning.js";

const debug = debugFn("vision-frontend:PlaceOfSafetyPicker");

export type PlaceOfSafetyPickerProps = {
  fieldName: string;
  label?: TranslationKeyAndOptions;
  incidentWorkItem?: IncidentWorkItemWithContext;
  filter?: Parameters<typeof usePlacesOfSafety>[0]["filter"];
  /**
   * When true, hides the "enter manually" toggle so only curated places of safety
   * can be selected. Used by the admin re-link flow, whose purpose is to link an
   * ad-hoc entry to a curated record.
   */
  disableManualEntry?: boolean;
} & Pick<ComponentProps<typeof Typeahead>, "autoFocus">;

type PlaceOfSafetyPickerOutput =
  | AdHocPlaceOfSafetyUnbranded
  | CuratedPlaceOfSafetyUnbranded
  | null
  | undefined
  | "";

const isDefined = (value: PlaceOfSafetyPickerOutput) => {
  return value !== null && value !== undefined && value !== "";
};

/**
 * Component that displays a warning banner when a Place of Safety
 * is outside the officer's force area.
 */
const OutsideForceAreaWarningBanner = ({
  postalCode,
  translationOptions,
}: {
  postalCode: string | undefined;
  translationOptions?: TranslationOptions;
}) => {
  const { showWarning } = useOutsideForceAreaWarning(postalCode);

  if (!showWarning) {
    return null;
  }

  return (
    <div data-testid="outside-force-area-warning">
      <Banner
        bannerType="warning"
        body={{
          translationKey: tk("placeOfSafetyPicker.outsideForceAreaWarning"),
          translationOptions,
        }}
      />
    </div>
  );
};

export const PlaceOfSafetyPicker = ({
  fieldName,
  label,
  incidentWorkItem,
  filter,
  autoFocus,
  disableManualEntry = false,
}: PlaceOfSafetyPickerProps) => {
  const [fieldInput, meta, helpers] =
    useField<PlaceOfSafetyPickerOutput>(fieldName);

  const [manuallyInput, setManuallyInput] = useState(
    !disableManualEntry &&
      isDefined(fieldInput.value) &&
      !("id" in fieldInput.value),
  );
  const [selected, setSelected] = useState(
    isDefined(fieldInput.value) && "name" in fieldInput.value && !manuallyInput
      ? fieldInput.value.name
      : "",
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { t } = useTranslation();
  const placesOfSafety = usePlacesOfSafety({ filter });

  // Get workflow context for translations
  const tOptions = useMemo(
    () => ({ context: getWorkflowTranslationContext({ incidentWorkItem }) }),
    [incidentWorkItem],
  );

  const sortedPlacesOfSafety = useMemo(
    () =>
      placesOfSafety.placesOfSafety
        .map((place) => ({
          ...place,
          name: place.name.replace(/\s+/g, " ").trim(),
        }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, "en-GB", {
            // We will prefer uppercase letters first, because in the context
            // of a Proper Noun, they are more likely to be the desired spelling
            caseFirst: "upper",
            sensitivity: "variant",
          }),
        ),
    [placesOfSafety.placesOfSafety],
  );

  const adHocPlaceOfSafetyState = useAdHocPlaceOfSafety(
    isDefined(fieldInput.value)
      ? {
          name: fieldInput.value.name,
          address: fieldInput.value.address,
          postalCode: fieldInput.value.postalCode,
        }
      : {},
  );
  const { name, address, postalCode } = adHocPlaceOfSafetyState;

  useEffect(() => {
    if (manuallyInput) {
      const runAsync = async () => {
        await helpers.setValue({
          name,
          address,
          postalCode,
        } satisfies AdHocPlaceOfSafetyUnbranded);
        await helpers.setTouched(true);
      };

      void runAsync();
    }
  }, [manuallyInput, name, address, postalCode, helpers]);

  // Memoised on the list (not the query) so the index is built once and only
  // re-searched per keystroke.
  const searchPlacesOfSafety = useMemo(
    () => createPlaceSearch(sortedPlacesOfSafety, (place) => place.name),
    [sortedPlacesOfSafety],
  );

  const getPlaceOfSafetyOptions = useMemo(
    () => searchPlacesOfSafety(selected, 8),
    [searchPlacesOfSafety, selected],
  );

  const onChangeInput = useCallback(
    (inputValue: string) => {
      setSelected(inputValue);

      // Normalise the input once rather than per candidate.
      const normalisedInput = normaliseForSearch(inputValue);

      // An empty (or punctuation-only) input means the user cleared the field
      // or has not typed a real query yet — not a "not found" case.
      if (normalisedInput.length === 0) {
        void helpers.setValue("");
        setErrorMessage("");
        return;
      }

      const match = sortedPlacesOfSafety.find(
        (pos) => normaliseForSearch(pos.name) === normalisedInput,
      );
      if (match) {
        void helpers.setValue(match);
        setErrorMessage("");
        return;
      }

      void helpers.setValue("");
      // While the curated list is still loading or failed to load, an absent
      // match means "we don't know yet" rather than "not a real place" — so
      // don't push the user towards manual entry with a spurious error.
      setErrorMessage(
        placesOfSafety.isLoading || placesOfSafety.isError
          ? ""
          : t("placeOfSafetyPicker.errorNotFound", tOptions),
      );
    },
    [
      sortedPlacesOfSafety,
      helpers,
      t,
      tOptions,
      placesOfSafety.isLoading,
      placesOfSafety.isError,
    ],
  );

  // Commit the picked option directly. Recovering it from the input label via
  // onChangeInput is fragile against the Typeahead's controlled value/input
  // sync and could leave the field uncommitted. Typing still goes through
  // onChangeInput; a string/null here is free-text or a cleared field.
  const onSelectOption = useCallback(
    (value: (typeof sortedPlacesOfSafety)[number] | string | null) => {
      if (value === null || typeof value === "string") {
        return;
      }
      setSelected(value.name);
      setErrorMessage("");
      void helpers.setValue(value);
    },
    [helpers],
  );

  return (
    <FormControl fullWidth data-testid={`formField-${fieldName}`}>
      <Typeahead
        label={label}
        name={fieldName}
        fieldName={fieldName}
        options={getPlaceOfSafetyOptions}
        optionsKey="id"
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        // Options are already fuzzy-filtered in getPlaceOfSafetyOptions, so
        // bypass MUI's built-in (punctuation-sensitive) substring filter, which
        // would otherwise cull fuzzy matches such as "Queens" -> "Queen's".
        filterOptions={(options) => options}
        onInputChange={onChangeInput}
        onChange={onSelectOption}
        disabled={manuallyInput}
        loading={placesOfSafety.isLoading && !manuallyInput}
        inputValue={selected}
        errorMessage={errorMessage}
        autoFocus={autoFocus}
      />
      {placesOfSafety.isError && !manuallyInput && (
        <div data-testid="place-of-safety-load-error">
          <Banner
            bannerType="error"
            body={{
              translationKey: tk("placeOfSafetyPicker.loadError"),
              translationOptions: tOptions,
            }}
          />
          <Button
            onClick={() => {
              placesOfSafety.refetch();
            }}
            data-testid="place-of-safety-retry"
            size="small"
          >
            {t(tk("placeOfSafetyPicker.retry"), tOptions)}
          </Button>
        </div>
      )}

      {!disableManualEntry && (
        <FormControlLabel
          label={t(
            tk("placeOfSafetyPicker.manuallyEnter") satisfies TranslationKey,
            tOptions,
          )}
          control={
            <Switch
              checked={manuallyInput}
              onChange={() => {
                const runAsync = async () => {
                  setManuallyInput(!manuallyInput);
                  adHocPlaceOfSafetyState.setName("");
                  adHocPlaceOfSafetyState.setAddress("");
                  adHocPlaceOfSafetyState.setPostalCode("");
                  setSelected("");
                  await helpers.setValue("");
                };
                void runAsync();
              }}
              slotProps={{ input: { "aria-label": "controlled" } }}
              data-testid="manual-switch"
            />
          }
        />
      )}

      {manuallyInput && (
        <CollectAdHocPlaceOfSafety
          {...adHocPlaceOfSafetyState}
          translationOptions={tOptions}
        />
      )}

      {meta.error && meta.touched && (
        <FormError
          data-testid="place-of-safety-error"
          error={
            !manuallyInput
              ? t(tk("placeOfSafetyPicker.selectManualEnter"), tOptions)
              : typeof meta.error === "object"
                ? Object.values(meta.error).join(", ")
                : meta.error
          }
        />
      )}

      {typeof fieldInput.value === "object" &&
        fieldInput.value !== null &&
        "id" in fieldInput.value &&
        "address" in fieldInput.value &&
        "postalCode" in fieldInput.value && (
          <Stack>
            <FormLabel
              translationKey={tk("placeOfSafetyPicker.address")}
              translationOptions={tOptions}
            />
            <Typography>{fieldInput.value.address}</Typography>
            <FormLabel
              translationKey={tk("placeOfSafetyPicker.postalCode")}
              translationOptions={tOptions}
            />
            <Typography>{fieldInput.value.postalCode}</Typography>
          </Stack>
        )}

      <OutsideForceAreaWarningBanner
        postalCode={
          typeof fieldInput.value === "object" &&
          fieldInput.value !== null &&
          "postalCode" in fieldInput.value
            ? fieldInput.value.postalCode
            : undefined
        }
        translationOptions={tOptions}
      />

      {debug.enabled && (
        <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
          {JSON.stringify(fieldInput.value)}
        </Typography>
      )}
    </FormControl>
  );
};
